import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const icons = {
  dashboard: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  entry: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5"/><path d="M17.5 3.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 8.5-8.5z"/></svg>,
  history: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  reports: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>,
  products: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>,
  users: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  factories: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 21h18M3 7v1a3 3 0 006 0V7m0 1a3 3 0 006 0V7m0 1a3 3 0 006 0V7H3l2-4h14l2 4M5 21V10.85M19 21V10.85"/></svg>,
  logout: <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>,
};

const roleLabels = { super_admin: 'Super Admin', manager: 'Διαχειριστής', operator: 'Χειριστής' };

export default function Layout({ children }) {
  const { user, selectedFactory, changeFactory, logoutUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logoutUser(); navigate('/login'); };

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h1>🏭 Factory Tracker</h1>
          <p>Παρακολούθηση Παραγωγής</p>
        </div>

        {user?.factories?.length > 1 && (
          <div className="sidebar-factory-selector">
            <label>Εργοστάσιο</label>
            <select
              value={selectedFactory?.id || ''}
              onChange={e => {
                const f = user.factories.find(f => f.id === parseInt(e.target.value));
                if (f) changeFactory(f);
              }}
            >
              {user.factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        )}

        {user?.factories?.length === 1 && (
          <div className="sidebar-factory-selector">
            <label>Εργοστάσιο</label>
            <div style={{ color: 'var(--gray-300)', fontSize: 13, padding: '4px 2px' }}>{selectedFactory?.name}</div>
          </div>
        )}

        <nav className="sidebar-nav">
          <div className="nav-section-label">Κύριο Μενού</div>
          <NavLink to="/" end className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {icons.dashboard} Αρχική
          </NavLink>
          <NavLink to="/entry" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {icons.entry} Καταχώρηση Ημέρας
          </NavLink>
          <NavLink to="/history" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {icons.history} Ιστορικό
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {icons.reports} Αναφορές
          </NavLink>

          {(user?.role === 'manager' || user?.role === 'super_admin') && (
            <>
              <div className="nav-section-label" style={{ marginTop: 12 }}>Διαχείριση</div>
              <NavLink to="/products" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                {icons.products} Προϊόντα
              </NavLink>
            </>
          )}

          {user?.role === 'super_admin' && (
            <>
              <NavLink to="/users" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                {icons.users} Χρήστες
              </NavLink>
              <NavLink to="/factories" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                {icons.factories} Εργοστάσια
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.full_name}</div>
              <div className="sidebar-user-role">{roleLabels[user?.role]}</div>
            </div>
            <button className="logout-btn" onClick={handleLogout} title="Αποσύνδεση">
              {icons.logout}
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
}
